(() => {
  if (typeof window.customElements.get('bundle-atc-form') !== 'undefined') { return }

  /* -------- UTF-8-SAFE BASE64 --------
     btoa() only accepts Latin-1 and throws on characters such as ’ – — œ …
     which made "Add to cart" fail silently. The state is turned into UTF-8
     bytes first. is-custom.js's restoreBundleStateFromURL() decodes it the
     same way and still reads bundles saved in the old format. */
  const encodeState = (state) => {
    const bytes = new TextEncoder().encode(JSON.stringify(state))
    let binary = ''
    bytes.forEach(byte => { binary += String.fromCharCode(byte) })
    return btoa(binary)
  }

  window.customElements.define('bundle-atc-form', class BundleAddToCartForm extends HTMLElement {
    constructor () {
      super()

      this.widgetSubscriptionOption  = this.querySelector('[data-bsw-subscription-option]')
      this.subscriptionOptionChecked = this.widgetSubscriptionOption.querySelector('input[id="subscription"]')
      this.widgetSelector            = this.querySelector('[data-bsw-select-selling-plan]')
      this.secondDeliveryDate        = this.querySelector('[data-second-delivery-date]')
      this.sellingPlanFrequencies    = this.widgetSelector.getAttribute('data-bsw-select-selling-plan') || ''
      this.bundleSellingPlans        = (this.getAttribute('data-product-selling-plan-ids') || '').split('|')
      this.bundleProductId           = this.querySelector('[data-bundle-product-id]')
      this.bundleVariantId           = this.querySelector('[data-selected-bundle-type-id]')
      this.atcButton                 = this.querySelector('[data-bundle-submit]')
      this.checkoutButton            = this.querySelector('[data-bundle-checkout-submit]')

      this.bundleItems     = null
      this.bundleEditURLs  = []      // state URL(s) this form added during this page visit
      this.isSubmitting    = false   // blocks double clicks while a submit is running

      this.initSelectListener()
      this.initBundleSubmit()
    }

    /* -------- CART DRAWER VISIBILITY --------
       Opens/closes the quick cart through the theme's own controls, so the
       theme's openQuickCart()/close() run (scroll lock, focus trap,
       data-fluorescent-overlay-open="true", footer padding). The header cart
       icon's click handler opens the quick cart whenever one exists. The
       manual class toggling is only a fallback if those controls are missing. */
    toggleCartDrawer (show = true) {
      const quickCart = document.querySelector('.quick-cart')
      const drawer = document.querySelector('.quick-cart__wrapper')
      if (!quickCart || !drawer) return

      const isOpen = drawer.classList.contains('active')

      if (show) {
        if (isOpen) return
        const cartIcon = document.querySelector('[data-js-cart-icon]')
        if (cartIcon) {
          cartIcon.click()
          return
        }
        drawer.classList.add('active')
        quickCart.classList.add('animation--quick-cart-revealed')
        document.body.setAttribute('data-fluorescent-overlay-open', 'true')
      } else {
        if (!isOpen) return
        const closeButton = document.querySelector('.quick-cart__close-icon')
        if (closeButton) {
          closeButton.click()
          return
        }
        drawer.classList.remove('active')
        document.body.setAttribute('data-fluorescent-overlay-open', 'false')
      }
    }

    /* -------- WAIT FOR THE QUICK CART TO RE-RENDER --------
       theme.js re-renders the drawer when it gets apps:product-added-to-cart
       (from an empty cart it replaces the whole drawer content). Opening the
       drawer before that finishes left an invisible layer over the page after
       closing it, so the next click on the page did nothing. The theme itself
       waits before opening for the same reason; this waits for the actual
       re-render (or 2.5s at most). Create it BEFORE dispatching the event. */
    waitForQuickCartRefresh (timeout = 2500) {
      const target = document.querySelector('.quick-cart')
      if (!target) return Promise.resolve()
      return new Promise(resolve => {
        let done = false
        const finish = () => {
          if (done) return
          done = true
          observer.disconnect()
          clearTimeout(timer)
          setTimeout(resolve, 50)
        }
        const observer = new MutationObserver(finish)
        observer.observe(target, { childList: true, subtree: true })
        const timer = setTimeout(finish, timeout)
      })
    }

    /* -------- ERROR MESSAGE UNDER THE BUTTON -------- */
    clearError () {
      this.querySelectorAll('[data-bundle-error]').forEach(el => el.remove())
    }

    showError (message) {
      this.clearError()
      const visibleButton = [this.atcButton, this.checkoutButton]
        .find(btn => btn && !btn.classList.contains('bundle-product__cart-submit--hidden')) || this.atcButton
      const el = document.createElement('p')
      el.className = 'bundle-product__error fs-body-75'
      el.setAttribute('role', 'alert')
      el.setAttribute('data-bundle-error', '')
      el.textContent = message
      visibleButton.insertAdjacentElement('afterend', el)
    }

    /* -------- "UPDATE MY CART" LABEL --------
       After any successful add, the next click on this button REPLACES the
       bundle just added (see the cleanup in submitBundle), so the label says
       so straight away instead of only after the second click. */
    setUpdateLabel () {
      const proceedLabel = this.atcButton?.querySelector('[data-bundle-atc-button-state="proceed"]')
      if (!proceedLabel) return
      proceedLabel.textContent = this.atcButton.dataset.checkoutButtonUpdateLabel
        || (window.Shopify.locale === 'fr' ? 'Mettre à jour mon panier' : 'Update my cart')
    }

    /* -------- SERIALISE CURRENT BUNDLE STATE --------
       Only what restoreBundleStateFromURL() needs is stored. Titles, image
       URLs and prices were dropped: they were never read back, made the line
       item property much longer, and titles were what made btoa() crash.
       `k` (the submit's timestamp) makes every add's URL unique, so two
       identical bundles added on separate visits stay two separate bundles
       in the cart (grouping, Edit and Remove all key on this URL). */
    generateBundleStateURL (stamp) {
      const bundleState = []

      this.querySelectorAll('bundle-item').forEach(item => {
        const qtyInput = item.querySelector('[data-bqs-input]')
        bundleState.push({
          id       : item.dataset.bundleItemId,
          cid      : item.dataset.bundleItemCollectionId,
          quantity : qtyInput.value,
          type     : item.dataset.bundleItemType
        })
      })

      const selectedBundleType = this.querySelector('[data-selected-bundle-type-id]')
      const bundleTypeId = selectedBundleType ? selectedBundleType.dataset.selectedBundleTypeId : ''

      const subscriptionChecked = this.subscriptionOptionChecked.checked
      const sellingPlanSelect   = this.querySelector('[data-bsw-selling-plan-select]')
      const sellingPlanPosition = sellingPlanSelect ? sellingPlanSelect.selectedIndex : 0
      const sellingPlanId       = subscriptionChecked && sellingPlanSelect ? sellingPlanSelect.value : ''

      const stateBase64 = encodeState({
        items          : bundleState,
        bundleTypeId,
        subscription   : subscriptionChecked,
        sellingPlanId,
        sellingPlanPos : sellingPlanPosition,
        k              : stamp
      })

      /* encodeURIComponent: a raw "+" in the base64 turns into a space when
         read back with URLSearchParams, which corrupted the Edit restore. */
      return `${window.location.pathname}?bundle=${encodeURIComponent(stateBase64)}`
    }

    /* -------- UPDATE "NEXT DELIVERY" DATE WHEN FREQUENCY CHANGES -------- */
    initSelectListener () {
      const monthFrequencies = this.sellingPlanFrequencies.split(',')

      const updateDate = () => {
        const pos = parseInt(this.widgetSelector.value)
        const rawFrequency = monthFrequencies[pos]
        /* No frequency at this position (e.g. Wholesale, where the setting
           is blank and the Subscription option is hidden). Nothing to show. */
        if (!rawFrequency) return
        const months = parseInt(rawFrequency.trim())
        if (isNaN(months)) return

        const today = new Date()
        const locale = (window.Shopify.locale === 'fr' || window.location.pathname.includes('/fr/'))
          ? 'fr-FR' : 'en-US'

        /* Clamp the day to the target month's length: new Date(y, m + 1, 31)
           rolls over (Jan 31 + 1 month showed Mar 3). */
        const future = new Date(today.getFullYear(), today.getMonth() + months, 1)
        const lastDayOfMonth = new Date(future.getFullYear(), future.getMonth() + 1, 0).getDate()
        future.setDate(Math.min(today.getDate(), lastDayOfMonth))

        this.secondDeliveryDate.textContent = ' ' + future.toLocaleDateString(locale, {
          month : 'short', day : 'numeric', year : 'numeric'
        })
      }

      updateDate()
      this.widgetSelector.addEventListener('change', updateDate)
    }

    /* -------- HANDLE BUNDLE SUBMIT -------- */
    initBundleSubmit () {
      this.atcButton.addEventListener('click', async (e) => {
        e.preventDefault()
        await this.submitBundle({ goToCheckout: false })
      })

      if (this.checkoutButton) {
        this.checkoutButton.addEventListener('click', async (e) => {
          e.preventDefault()
          await this.submitBundle({ goToCheckout: true })
        })
      }
    }

    /* -------- SHARED ADD-TO-CART LOGIC, WITH OR WITHOUT A CHECKOUT REDIRECT --------
       Everything is inside try/catch now. Before, the Recharge call and the
       state encoding ran outside it, so any error there meant nothing was
       added and the customer saw nothing at all. */
    async submitBundle ({ goToCheckout }) {
      if (this.isSubmitting) return
      this.isSubmitting = true

      const button = goToCheckout && this.checkoutButton ? this.checkoutButton : this.atcButton
      const genericError = this.dataset.errorMessage || 'Something went wrong. Please try again.'
      let redirecting = false

      button.classList.add('loading')
      button.setAttribute('aria-busy', 'true')
      this.clearError()
      this.toggleCartDrawer(false)

      try {
        if (typeof window.recharge === 'undefined' || !window.recharge.bundle) {
          throw new Error('The Recharge storefront script (recharge-client) is not loaded.')
        }

        this.bundleItems = this.querySelectorAll('bundle-item')
        const pos = parseInt(this.widgetSelector.value)
        const isSubscription = this.subscriptionOptionChecked.checked

        /* ------- Build Recharge bundle descriptor ------- */
        const bundleProductData = {
          productId : this.bundleProductId.getAttribute('data-bundle-product-id'),
          variantId : this.bundleVariantId.getAttribute('data-selected-bundle-type-id'),
          handle    : this.bundleVariantId.getAttribute('data-selected-bundle-parent'),
          ...(isSubscription && {
            sellingPlan : this.bundleSellingPlans[pos]
          })
        }

        const bundleDescriptor = {
          externalVariantId  : bundleProductData.variantId,
          externalProductId  : bundleProductData.productId,
          selections         : []
        }

        this.bundleItems.forEach(item => {
          const qty = parseInt(item.querySelector('[data-bqs-input]').value, 10)
          if (!qty || qty < 1) return

          let planId = null
          if (item.dataset.bundleItemSellingPlanIds && isSubscription) {
            planId = item.dataset.bundleItemSellingPlanIds.split('|')[pos]
          }

          const entry = {
            collectionId      : item.dataset.bundleItemCollectionId,
            externalProductId : item.dataset.bundleItemId,
            externalVariantId : item.dataset.bundleItemId,
            quantity          : qty
          }
          if (planId) entry.sellingPlan = planId
          bundleDescriptor.selections.push(entry)
        })

        /* ------- Get the actual Shopify line-items for this bundle ------- */
        const cartItems = await window.recharge.bundle.getDynamicBundleItems(bundleDescriptor, bundleProductData.handle)

        /* Stamp helper props onto each line-item so we can find/remove later */
        const stamp = Date.now()
        const bundleStateURL = this.generateBundleStateURL(stamp)
        cartItems.forEach(li => {
          li.properties = {
            ...(li.properties || {}),
            _key             : `${li.id}:${stamp}`,
            _bundle_item     : 'bundle_item',
            _bundle_state_url: bundleStateURL
          }
        })

        /* ------- Add NEW version -------
           (The old "delete previous version" loop that ran here, BEFORE the
           add, was removed: it sent a URL as the line id to /cart/change.js,
           so it always failed. The old version is removed below, only after
           the new one was added successfully.) */
        const addRes = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body   : JSON.stringify({ items: cartItems })
        })

        if (!addRes.ok) {
          /* Shopify's description is customer-friendly and already
             translated (e.g. "only 2 left"), so it is shown as-is. The cart
             is untouched at this point. */
          const errorData = await addRes.json().catch(() => ({}))
          const error = new Error(errorData.description || errorData.message || genericError)
          error.customerMessage = errorData.description || genericError
          throw error
        }

        /* What this form added on its previous submit during this page visit
           (in-place re-edit without the pencil "Modify" link). */
        const previousBundleURLs = this.bundleEditURLs
        this.bundleEditURLs = [bundleStateURL]

        try {
          /* ------- Clean up the old version of this bundle in the cart -------
             A line is the old version when its _bundle_state_url matches the
             page's own URL (reached via the pencil "Modify" link) or what
             this form added on a previous submit. Lines from this submit are
             skipped thanks to the stamp. */
          const cart       = await fetch(`${window.Shopify.routes.root}cart.js`).then(r => r.json())
          const updates    = {}
          const currentURL = window.location.pathname + window.location.search

          cart.items.forEach(item => {
            const props = item.properties || {}
            const propStamp = props._key ? String(props._key).split(':').pop() : null
            const matchesPageURL   = props._bundle_state_url === currentURL
            const matchesPriorEdit = previousBundleURLs.includes(props._bundle_state_url)

            if ((matchesPageURL || matchesPriorEdit) && item.key && propStamp !== String(stamp)) {
              updates[item.key] = 0
            }
          })

          if (Object.keys(updates).length) {
            await fetch(`${window.Shopify.routes.root}cart/update.js`, {
              method : 'POST',
              headers: { 'Content-Type': 'application/json' },
              body   : JSON.stringify({ updates })
            })

            /* Drop the old bundle card from the drawer right away; the
               theme's refresh below re-renders it anyway. */
            document.querySelectorAll('[data-input-item]').forEach(node => {
              if (currentURL === node.dataset.key || previousBundleURLs.includes(node.dataset.key)) {
                node.remove()
              }
            })
          }
        } catch (cleanupErr) {
          /* The new bundle IS in the cart; only the old copy may remain.
             Not shown to the customer, the drawer will show both. */
          console.error('Bundle cleanup error:', cleanupErr)
        }

        this.setUpdateLabel()

        /* Refreshes the quick cart (theme.js listens for this DOM event) */
        const quickCartRefreshed = this.waitForQuickCartRefresh()
        document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'))

        if (goToCheckout) {
          redirecting = true
          window.location.href = `${window.Shopify.routes.root}checkout`
          return
        }

        await quickCartRefreshed
        this.toggleCartDrawer(true)
      } catch (err) {
        console.error('Bundle add error:', err)
        this.showError(err.customerMessage || genericError)
      } finally {
        this.isSubmitting = false
        if (!redirecting) {
          button.classList.remove('loading')
          button.removeAttribute('aria-busy')
        }
      }
    }
  })
})()